import { track } from "@/features/analytics";
import type { useGeneratePdfMutation } from "@/shared/services";
import {
  EXPORT_OUTPUT_FORMAT,
  type TExportPdfData,
  type TPdfObj,
} from "../types";

interface IGenPdfProps {
  pdfData: TExportPdfData;
  orgLogo?: string | null;
  abortController: AbortController;
  handleGeneratePdf: ReturnType<typeof useGeneratePdfMutation>["mutateAsync"];
}

export const generatePdf = async ({
  pdfData,
  orgLogo,
  abortController,
  handleGeneratePdf,
}: IGenPdfProps) => {
  const { removeQRCode, highquality, slides, minPageRatio, version } = pdfData;
  const format = EXPORT_OUTPUT_FORMAT[pdfData.format ?? "pdf"];
  // A deck is one page per slide: PowerPoint (and Google Slides, which is a
  // PowerPoint render uploaded to Drive) always renders multi-slide.
  const layout = format === "pptx" ? "multi-slide" : pdfData.layout;

  const url = new URL(version.url);

  const searchParams = new URLSearchParams({
    preview: "true",
    printToPdf: "true",
    storyShortUrl: version.shortUrl,
    pdfRemoveSdBranding: (removeQRCode ? "true" : "false") as string,
    pdfPrintMode: (layout === "a4-letter"
      ? "many-pages"
      : "single-page") as string,
  });

  if (orgLogo) searchParams.set("sdOrgLogo", orgLogo);

  const raw: Record<string, unknown> = {
    versionId: version._id,
    waitUntil: "load",
    waitUntilDelay: 4000,
    // Raced against waitUntilDelay by the renderer — early settle on current
    // templates, identical 4s wait on stories that never emit the class.
    waitUntilSelector: ".sd-template-loaded",
    // Same render job also captures a JPEG cover screenshot
    // (response.screenshotLink) shown by the success toast. It is shot from
    // screenshotUrl — the NORMAL-mode og-preview page (same query string as
    // use-generate-preview's makeOptions) — never from the printToPdf page:
    // print mode lays out at 210mm, so a viewport clip of it renders
    // half-width content on a white field.
    screenshotOptions: {},
    // Kept in sync with dotell-backend exports/pdf-version-payload.js
    // #buildCoverScreenshotUrl (selected-slides=2, no optimize).
    screenshotUrl: `${version.url}?analytics=false&static=true&progressBar=false&engagementBar=false&selected-slides=2`,
    pdfOptions: {
      screenWidth: 1920,
      screenHeight: 1080,
      landscape: false,
      width: "210mm",
      format: layout === "a4-letter" ? "A4" : undefined,
      printBackground: true,
      timeout: 0,
      scale: 1,
      slides,
      minPageRatio,
      layout,
      highQuality: highquality,
      quality: pdfData.quality,
      // Non-PDF formats: headless post-processes the same render ("pptx" =
      // uniform 16:9 pages + ConvertAPI, "images" = one image per page in a
      // zip) and returns pptxLink / imagesLink instead of pdfLink.
      ...(format !== "pdf" ? { outputFormat: format } : {}),
    },
  };

  if (version.seal) {
    const [data] = version.seal;

    for (const [key, value] of Object.entries(data)) {
      searchParams.set(key, value as string);
    }
    searchParams.set("sealPDF", "true");
  }

  url.search = searchParams.toString();
  const pdfUrl = url.toString();
  raw.url = pdfUrl;

  try {
    const response = await handleGeneratePdf({
      payload: raw,
      config: { signal: abortController.signal },
    });

    const link = response?.[`${format}Link`];
    if (!link) throw Error(`${format} link is null`);

    const pdfObj: TPdfObj = {
      url: link,
      previewUrl: response.screenshotLink ?? undefined,
      removeBranding: removeQRCode,
      printMode: layout === "a4-letter" ? "many-pages" : "single-page",
      createdAt: Date.now(),
    };

    return pdfObj;
  } catch (e) {
    // A user-cancelled export is not a failure — skip the analytics noise.
    if (abortController.signal.aborted) return null;
    track("failed PDF generation", {
      // Query params stay out of analytics — sealed versions carry signer
      // fields in them.
      "raw request": { ...raw, url: undefined },
      "source url": version?.url,
      "request url": `${url.origin}${url.pathname}`,
      "error message": (e as Error).toString(),
    });
    return null;
  }
};
